import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { AccountCreation } from '@/components/signup/account-creation/account-creation';
import { Input } from '@/components/signup/account-creation/components/input/input.tsx';
import { Social } from '@/components/signup/account-creation/components/social/social.tsx';
import { useJsmPlaygroundSignupHref } from '@/components/signup/hooks/use-jsm-playground-signup-href';
import { SocialProvider } from '@/components/signup/utils/get-social-signup-button-urls.tsx';
import { wacToken } from '@/libraries/design/foundations';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';
import { useLocation } from '@/utils/location-provider';
import { getWacIntegrationUrl } from '@/utils/urls.ts';
import { useWacRefresh } from '@/utils/wac-refresh/wac-refresh-context';

import { InlineInputSubmitSocialEmbeddedSignup } from './components/inline-account-creation/inline-input-submit-social-embedded-signup.tsx';
import { InlineInputSubmitSocial } from './components/inline-account-creation/inline-input-submit-social.tsx';
import { InlineInputSubmit } from './components/inline-account-creation/inline-input-submit.tsx';

export type SignUpFormTextColor = 'default' | 'light';

export type InlineSubmitVersion = 'inlineSubmit' | 'inlineSocialSubmit' | 'embeddedSignup';

export type UniformSignUpProps = {
	socialProviders?: SocialProvider[];
	helpText?: string;
	textColor?: SignUpFormTextColor;
	/**
	 * @deprecated Use `inlineSubmitVersion` instead. This boolean is still supported
	 * for backwards compatibility until the TWC inline signup form is switched over
	 * to use `inlineSubmitVersion="inlineSocialSubmit"`. Once migrated, this prop
	 * will be removed.
	 */
	inline?: boolean;
	/**
	 * Controls which version of the signup form to render.
	 * - `undefined` (default): renders the standard form with separate input, submit, and social
	 * - `'inlineSubmit'`: renders InlineInputSubmit (inline email + submit) with social stacked below
	 * - `'inlineSocialSubmit'`: renders InlineInputSubmitSocial (inline email + submit + social)
	 * - `'embeddedSignup'`: compact embedded form when `sparkJiraPtEmbeddedSignup` experiment is on
	 *   (or in `/uniform-preview` so Canvas editors can see it); falls back to inlineSocialSubmit
	 *   when not enrolled outside preview (TWCG-2414)
	 */
	inlineSubmitVersion?: InlineSubmitVersion;
	continueUrl?: string;
};

export const transformProviders = (providerList: string[]) => {
	return providerList.reduce((previous, key) => ({ ...previous, [key.toLowerCase()]: key }), {});
};

const SERVICE_COLLECTION_COBRANDING = 'wac--service-collection-jsm-cobranding';
const JSM_SERVICE_MANAGEMENT_PATH = '/software/jira/service-management';

const UniformSignUpForm = ({
	socialProviders,
	helpText,
	textColor = 'default',
	inline = false,
	inlineSubmitVersion,
	continueUrl,
}: // eslint-disable-next-line complexity
UniformSignUpProps) => {
	const intl = useIntl();
	const isWacRefresh = useWacRefresh();
	const location = useLocation();
	const playgroundContinueUrl = useJsmPlaygroundSignupHref(continueUrl ?? '');

	const isJsmServiceManagementPage = location.pathname.includes(JSM_SERVICE_MANAGEMENT_PATH);

	const getContinueUrl = playgroundContinueUrl
		? () =>
				playgroundContinueUrl.startsWith('/')
					? getWacIntegrationUrl() + playgroundContinueUrl
					: playgroundContinueUrl
		: undefined;

	const defaultHelpText = intl.formatMessage({
		id: 'signupForm.accountCreation.helperTextTeam26',
		defaultMessage: 'Use a work email to find teammates and get access to Rovo AI',
	});
	const configuredHelpText = helpText?.trim();
	const emailHelpText = configuredHelpText || defaultHelpText;

	const sparkJiraPtEmbeddedSignup = useExperiment(experiments.sparkJiraPtEmbeddedSignup);
	const isEmbeddedSignupEnabled =
		sparkJiraPtEmbeddedSignup.type === 'enrolled' && sparkJiraPtEmbeddedSignup.value === true;
	// Uniform Canvas preview has no Statsig enrollment — show embedded signup when selected
	const isUniformPreview = location.pathname.includes('/uniform-preview');

	const useInlineInput = inlineSubmitVersion === 'inlineSubmit';
	const wantsEmbeddedSignup = inlineSubmitVersion === 'embeddedSignup';
	const useEmbeddedSignup = wantsEmbeddedSignup && (isEmbeddedSignupEnabled || isUniformPreview);
	const useInlineSocial =
		inline || inlineSubmitVersion === 'inlineSocialSubmit' || wantsEmbeddedSignup;

	return (
		<div css={[textColor === 'light' && lightTextColorCss]}>
			<AccountCreation
				identityCobranding={isJsmServiceManagementPage ? SERVICE_COLLECTION_COBRANDING : undefined}
				getContinueUrl={getContinueUrl}
				Login={null}
				Separator={useInlineSocial ? null : undefined}
				Error={useInlineInput || useInlineSocial ? null : undefined}
				Input={
					useInlineInput ? (
						<InlineInputSubmit helpText={configuredHelpText} darkMode={textColor === 'light'} />
					) : useEmbeddedSignup ? (
						<InlineInputSubmitSocialEmbeddedSignup />
					) : useInlineSocial ? (
						<InlineInputSubmitSocial helpText={configuredHelpText} />
					) : isWacRefresh ? (
						<Input helpText={emailHelpText} />
					) : undefined
				}
				Submit={useInlineInput || useInlineSocial ? null : undefined}
				Social={
					useInlineSocial ? null : socialProviders?.length ? (
						<div css={useInlineInput && socialButtonHeightCss}>
							<Social providers={transformProviders(socialProviders)} />
						</div>
					) : undefined
				}
			/>
		</div>
	);
};

export default UniformSignUpForm;

const socialButtonHeightCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '44px',
	},
});

const lightTextColorCss = css({
	color: wacToken('color.neutral.light0'),
});
